import type {
  AdminGalleryBucket,
  AdminGalleryCell,
  AdminGalleryRange,
  AdminGallerySeries,
} from '../api/client.ts';
import { formatFileSize } from './format.ts';

/**
 * The arithmetic behind the «что приходило» chart, kept out of the component
 * that draws it.
 *
 * Everything here is a pure function of the payload: the scale, the stacking,
 * which columns get a label under them and what that label says. It is in
 * `lib/` for the reason the whole of `lib/` exists — a scale that rounds 7 to
 * «10» and a stack whose segments add up to something other than the bar are
 * both bugs you can only see by reading numbers, and reading numbers is what a
 * test does better than a person looking at a screenshot.
 */

/** What a bar is measuring. Never both at once — see `stack`. */
export type GalleryChartMode = 'count' | 'bytes';

export interface GallerySeriesInfo {
  key: AdminGallerySeries;
  tier: 'thumb' | 'preview' | 'original';
  kind: 'photo' | 'video';
  /** For the legend and the tooltip, where the tier is already named. */
  label: string;
}

/**
 * The six series in stacking order, which is also colour order.
 *
 * Ordered by tier and then by kind rather than by size, so the stack reads
 * bottom-up in the order the work actually happens: a thumbnail, then the
 * preview compressed from it, then the original if anybody asked. A stack
 * sorted by height would reorder itself whenever a phone had a busy evening.
 *
 * The pairing is why the palette works — a tier is a hue and a kind is a step
 * of it, so this order puts the two steps of each hue next to each other. See
 * AdminGalleryChart.css.
 */
export const GALLERY_CHART_SERIES: GallerySeriesInfo[] = [
  { key: 'thumb.photo', tier: 'thumb', kind: 'photo', label: 'Миниатюры · фото' },
  { key: 'thumb.video', tier: 'thumb', kind: 'video', label: 'Миниатюры · видео' },
  { key: 'preview.photo', tier: 'preview', kind: 'photo', label: 'Превью · фото' },
  { key: 'preview.video', tier: 'preview', kind: 'video', label: 'Превью · видео' },
  { key: 'original.photo', tier: 'original', kind: 'photo', label: 'Оригиналы · фото' },
  { key: 'original.video', tier: 'original', kind: 'video', label: 'Оригиналы · видео' },
];

export const GALLERY_RANGE_LABEL: Record<AdminGalleryRange, string> = {
  halfHour: '30 мин',
  hour: '1 час',
  sixHours: '6 часов',
  day: '24 часа',
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

export function cellValue(cell: AdminGalleryCell, mode: GalleryChartMode): number {
  return mode === 'bytes' ? cell.bytes : cell.count;
}

export function bucketTotal(bucket: AdminGalleryBucket, mode: GalleryChartMode): number {
  return GALLERY_CHART_SERIES.reduce(
    (sum, series) => sum + cellValue(bucket.series[series.key], mode),
    0,
  );
}

/** `340 КБ` or `1 204` — a value in whichever unit the chart is currently in. */
export function formatValue(value: number, mode: GalleryChartMode): string {
  return mode === 'bytes' ? formatFileSize(value) : value.toLocaleString('ru-RU');
}

/** 1, 2 or 5 times a power of ten — the numbers people divide by. */
function decimalTop(want: number): number {
  let magnitude = 1;
  while (magnitude * 10 <= want) magnitude *= 10;
  for (const step of [1, 2, 5]) {
    if (magnitude * step >= want) return magnitude * step;
  }
  return magnitude * 10;
}

/**
 * The next power of two, which for bytes is what a round number is.
 *
 * Not the decimal ladder with 1024 for a base, which is what this was first:
 * that one has three steps between one power of 1024 and the next, so 180 МБ —
 * a single 4K video, the commonest large thing here — ran off the end of 8 МБ
 * and was drawn against a 1 ГБ axis with five sixths of the plot empty. Every
 * power of two above a kilobyte is a whole number of КБ, МБ or ГБ, so this
 * never produces a gridline the formatter has to round.
 */
function binaryTop(want: number): number {
  let top = 1;
  while (top < want) top *= 2;
  return top;
}

export interface GalleryChartScale {
  /** The value the top gridline stands for. Never below 1. */
  top: number;
  /** Every gridline from 0 up, the top one included. */
  ticks: number[];
}

/**
 * A top and a set of gridlines a person can do arithmetic against.
 *
 * The axis has to round *up* to a number the eye can divide, and the reason it
 * is a rule rather than `max * 1.1` is that an axis is read as a denominator: a
 * bar at four fifths of the height means something only when the top is worth
 * dividing by. Counts round to 1, 2 or 5 times a power of ten; bytes round to a
 * power of two, so a chart of megabytes gets megabyte gridlines instead of
 * 1 000 000 sitting one pixel under 1 МБ.
 *
 * An empty window still gets a scale — a chart with no gridlines at all reads
 * as broken rather than as quiet.
 */
export function niceScale(max: number, mode: GalleryChartMode = 'count'): GalleryChartScale {
  const want = Math.max(1, max);
  const top = mode === 'bytes' ? binaryTop(want) : decimalTop(want);

  // Whichever number of bands divides the top exactly, four for preference:
  // gridlines at 2.5 and 7.5 are worse than no gridlines, because they make the
  // reader do the arithmetic the axis was there to save them.
  const divisions = [4, 5, 2, 1].find((count) => top % count === 0) ?? 1;
  const ticks = Array.from({ length: divisions + 1 }, (_unused, index) => (top / divisions) * index);
  return { top, ticks };
}

export interface GalleryStackSegment {
  key: AdminGallerySeries;
  label: string;
  value: number;
  /** How much of the bar is below this segment, in the same unit. */
  offset: number;
}

/**
 * One bar, bottom to top, with the empty series left out.
 *
 * Zero-height segments are dropped rather than drawn at one pixel: they are the
 * ordinary case — most hours have no video originals in them — and a stack of
 * six hairlines under every bar is a chart that says a phone did something on
 * every one of thirty days when it did nothing at all.
 */
export function stack(bucket: AdminGalleryBucket, mode: GalleryChartMode): GalleryStackSegment[] {
  const segments: GalleryStackSegment[] = [];
  let offset = 0;
  for (const series of GALLERY_CHART_SERIES) {
    const value = cellValue(bucket.series[series.key], mode);
    if (value <= 0) continue;
    segments.push({ key: series.key, label: series.label, value, offset });
    offset += value;
  }
  return segments;
}

/**
 * What goes under a column: `14:15`, `14:00`.
 *
 * Always the clock, minutes included: no window here is longer than a day, so
 * the date is never what distinguishes two columns — it lives in the tooltip
 * and in the table — and the minutes are `00` on the hourly columns anyway,
 * which are aligned to the hour.
 */
export function bucketLabel(at: number): string {
  const date = new Date(at);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** «1 ч», «15 мин», «1 мин» — how wide a column is, for the note under the plot. */
export function bucketWidthLabel(bucketMs: number): string {
  if (bucketMs >= HOUR) return `${bucketMs / HOUR} ч`;
  return `${bucketMs / MINUTE} мин`;
}

/**
 * How far apart the labels under the columns are, in minutes.
 *
 * Whichever round step puts no more than eight of them across the window: 5 for
 * half an hour of minutes, 10 for an hour, 60 for six hours of quarters, 240 for
 * a day of hours. Derived from the window rather than listed per preset, so a
 * new preset cannot arrive without an axis — and the steps are the ones a clock
 * face already has, so a label never lands on `14:07`.
 */
const LABEL_STEPS_MIN = [5, 10, 15, 30, 60, 120, 240];

export function labelStepMs(bucketMs: number, count: number): number {
  const span = bucketMs * count;
  const step = LABEL_STEPS_MIN.find((minutes) => (minutes * MINUTE) * 8 >= span);
  return (step ?? LABEL_STEPS_MIN[LABEL_STEPS_MIN.length - 1]) * MINUTE;
}

/**
 * Which columns get a label under them.
 *
 * Not every one of them: thirty times along an axis this wide overlap into a
 * grey stripe, and the tooltip carries the exact span for the column somebody
 * is actually asking about. So a label lands on the round marks a reader is
 * already looking for — every five minutes, every ten, on the hour — as many
 * of them as the window has room for. Read off the column's own clock rather
 * than its index, so the marks stay on the round minute as the window slides.
 */
export function labelledBuckets(buckets: AdminGalleryBucket[], bucketMs: number): Set<number> {
  const marks = new Set<number>();
  const step = labelStepMs(bucketMs, buckets.length);
  buckets.forEach((bucket, index) => {
    const date = new Date(bucket.at);
    const sinceMidnight = (date.getHours() * 60 + date.getMinutes()) * MINUTE;
    if (sinceMidnight % step === 0) marks.add(index);
  });
  return marks;
}

/** «13.06, 18:00 — 19:00» — the exact span behind a hovered column. */
export function bucketSpan(at: number, bucketMs: number): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const clock = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  const start = new Date(at);
  const end = new Date(at + bucketMs);
  const day = `${pad(start.getDate())}.${pad(start.getMonth() + 1)}`;
  // Both ends off the clock rather than `HH:00` written out: the hourly
  // buckets start on the hour, so this reads `18:00 — 19:00` for them, and a
  // minute-wide column is `18:15 — 18:16` instead of an hour that begins and
  // ends at the same minute.
  return `${day}, ${clock(start)} — ${clock(end)}`;
}
